// v2/components/admin/AdminShell.js
// /v2/admin/* sarmalayicisi:
//  1) oturum yoksa login'e, yerel status admin degilse /v2'ye yonlendirir (on kontrol)
//  2) yoneticiligi backend'e sorar (api/v2/admin/me/) — asil karar sunucunun
//  3) yerlesim: masaustu sabit sol menu, tablet/mobil cekmece, mobil alt gezinme
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { FileText,
  LayoutDashboard, Store, ShoppingBag, Wallet, ShieldAlert, UserCog, LogIn, Percent,
  Megaphone, Menu, X, LogOut, ArrowLeftRight, History, RotateCw, Loader2, AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { ADMIN_NAV, ADMIN_BOTTOM_NAV } from '../../constants/adminNav';
import { oturumBilgisi, oturumuSonlandir, LOGIN_YOLU } from '../../utils/authStore';
import { apiGet, apiPost } from '../../utils/api';
import { basHarfler } from '../../utils/adminFormat';
import ScrollToTop from '../ScrollToTop';
import SiparisBildirimi from './SiparisBildirimi';
import s from './AdminShell.module.css';
import '../../styles/tokens.css';

const LOGO_SRC = '/asotomotivlogo.png';
const MUSTERI_PANELI = '/v2';

const ICONS = { FileText,
  LayoutDashboard, Store, ShoppingBag, Wallet, ShieldAlert, UserCog, LogIn, Percent, Megaphone,
};
const TUM_OGELER = ADMIN_NAV.flatMap((g) => g.items);

function eslesir(oge, yol) {
  const temiz = yol.length > 1 ? yol.replace(/\/+$/, '') : yol;
  if (oge.end) return temiz === oge.to;
  return temiz === oge.to || temiz.startsWith(`${oge.to}/`);
}

function aktifOge(yol) {
  return TUM_OGELER
    .filter((o) => eslesir(o, yol))
    .sort((a, b) => b.to.length - a.to.length)[0] || null;
}

function MenuOgesi({ oge, onSec }) {
  const Ikon = ICONS[oge.icon] || LayoutDashboard;
  if (!oge.ready) {
    return (
      <span className={`${s.link} ${s.linkOff}`} aria-disabled="true" title="Bu bölüm hazırlanıyor">
        <Ikon size={18} />
        <span className={s.linkLabel}>{oge.label}</span>
        <span className={s.soon}>Yakında</span>
      </span>
    );
  }
  return (
    <NavLink
      to={oge.to}
      end={Boolean(oge.end)}
      onClick={onSec}
      className={({ isActive }) => `${s.link} ${isActive ? s.linkOn : ''}`}
    >
      <Ikon size={18} />
      <span className={s.linkLabel}>{oge.label}</span>
    </NavLink>
  );
}

export default function AdminShell() {
  const konum = useLocation();
  const { girisli, user } = oturumBilgisi();
  const yerelYonetici = Boolean(user && user.status === 'admin');

  const [durum, setDurum] = useState('kontrol'); // kontrol | ok | yasak | hata
  const [yonetici, setYonetici] = useState(null);
  const [deneme, setDeneme] = useState(0);
  const [cekmece, setCekmece] = useState(false);
  const [logoOk, setLogoOk] = useState(true);

  // backend yetki dogrulamasi (sayfa gecislerinde tekrarlanmaz)
  useEffect(() => {
    if (!girisli || !yerelYonetici) return undefined;
    const ctrl = new AbortController();
    setDurum('kontrol');
    apiGet('/v2/admin/me/', { oturumsuz: true, signal: ctrl.signal })
      .then((v) => {
        if (ctrl.signal.aborted) return;
        if (v && v.success) {
          setYonetici(v.user || null);
          setDurum('ok');
        } else {
          setDurum('hata');
        }
      })
      .catch((e) => {
        if (ctrl.signal.aborted || (e && e.name === 'AbortError')) return;
        if (e && e.status === 401) { oturumuSonlandir(); return; }
        if (e && e.status === 403) { setDurum('yasak'); return; }
        setDurum('hata');
      });
    return () => ctrl.abort();
  }, [girisli, yerelYonetici, deneme]);

  // sayfa degisince cekmece kapansin
  useEffect(() => { setCekmece(false); }, [konum.pathname]);

  // cekmece acikken: ESC ile kapat + arka plan kaymasin
  useEffect(() => {
    if (!cekmece) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setCekmece(false); };
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [cekmece]);

  const aktif = useMemo(() => aktifOge(konum.pathname), [konum.pathname]);

  useEffect(() => {
    const onceki = document.title;
    document.title = `${aktif ? aktif.label : 'Yönetim'} · Yönetim Paneli`;
    return () => { document.title = onceki; };
  }, [aktif]);

  const kapat = useCallback(() => setCekmece(false), []);

  const cikis = useCallback(async () => {
    try {
      await apiPost('/logout/', { token: oturumBilgisi().token }, { oturumsuz: true });
    } catch (e) {
      /* sunucu cevap vermese de yerel oturum kapatilir */
    }
    oturumuSonlandir();
  }, []);

  if (!girisli) {
    return <Navigate to={LOGIN_YOLU} replace state={{ from: konum.pathname }} />;
  }
  if (!yerelYonetici || durum === 'yasak') {
    return <Navigate to={MUSTERI_PANELI} replace />;
  }
  if (durum === 'kontrol') {
    return (
      <div className={s.gate}>
        <div className={s.gateBox}>
          <Loader2 size={30} className={s.spin} />
          <p className={s.gateText}>Yetki doğrulanıyor…</p>
        </div>
      </div>
    );
  }
  if (durum === 'hata') {
    return (
      <div className={s.gate}>
        <div className={s.gateBox}>
          <AlertTriangle size={30} className={s.gateWarn} />
          <h1 className={s.gateTitle}>Sunucuya ulaşılamadı</h1>
          <p className={s.gateText}>Yetki bilgisi alınamadı. Bağlantınızı kontrol edip tekrar deneyin.</p>
          <button type="button" className={s.primaryBtn} onClick={() => setDeneme((n) => n + 1)}>
            <RotateCw size={16} /> Tekrar dene
          </button>
        </div>
      </div>
    );
  }

  const ad = (yonetici && yonetici.ad_soyad) || (user && user.ad_soyad) || 'Yönetici';
  const altOgeler = ADMIN_BOTTOM_NAV.map((id) => TUM_OGELER.find((o) => o.id === id)).filter(Boolean);

  return (
    <div className={s.root}>
      <aside
        className={`${s.sidebar} ${cekmece ? s.sidebarOpen : ''}`}
        aria-label="Yönetim menüsü"
      >
        <div className={s.brand}>
          {logoOk ? (
            <img src={LOGO_SRC} alt="AS Otomotiv" className={s.brandLogo} onError={() => setLogoOk(false)} />
          ) : (
            <span className={s.brandFallback}>AS</span>
          )}
          <span className={s.brandText}>
            <span className={s.brandTitle}>Yönetim Paneli</span>
            <span className={s.brandSub}>AS Otomotiv B2B</span>
          </span>
          <button type="button" className={s.closeBtn} onClick={kapat} aria-label="Menüyü kapat">
            <X size={20} />
          </button>
        </div>

        <nav className={s.nav}>
          {ADMIN_NAV.map((grup) => (
            <div key={grup.group} className={s.group}>
              <p className={s.groupTitle}>{grup.group}</p>
              {grup.items.map((oge) => (
                <MenuOgesi key={oge.id} oge={oge} onSec={kapat} />
              ))}
            </div>
          ))}
        </nav>

        <div className={s.sideFoot}>
          <a href={MUSTERI_PANELI} className={s.link}
             target="_blank" rel="noopener noreferrer" onClick={kapat}>
            <ArrowLeftRight size={18} />
            <span className={s.linkLabel}>Müşteri Paneli</span>
            <ExternalLink size={13} className={s.linkExt} />
          </a>
          <button type="button" className={`${s.link} ${s.linkDanger}`} onClick={cikis}>
            <LogOut size={18} />
            <span className={s.linkLabel}>Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {cekmece && <div className={s.backdrop} onClick={kapat} aria-hidden="true" />}

      <div className={s.main}>
        <header className={s.topbar}>
          <button
            type="button"
            className={`${s.iconBtn} ${s.menuBtn}`}
            onClick={() => setCekmece(true)}
            aria-label="Menüyü aç"
          >
            <Menu size={20} />
          </button>
          <div className={s.topText}>
            <span className={s.topCrumb}>Yönetim Paneli</span>
            <span className={s.topTitle}>{aktif ? aktif.label : 'Yönetim'}</span>
          </div>
          <div className={s.topRight}>
            <SiparisBildirimi />
            <span className={s.efWrap} title="EforInnovate">
              <img className={s.efLogo} src="/eforinnovate_beyazarka_web.png" alt="EforInnovate" />
            </span>
            <div className={s.userChip} title={(yonetici && yonetici.mail) || ''}>
              <span className={s.avatar}>{basHarfler(ad)}</span>
              <span className={s.userText}>
                <span className={s.userName}>{ad}</span>
                <span className={s.userRole}>Yönetici</span>
              </span>
            </div>
            <button
              type="button"
              className={`${s.iconBtn} ${s.logoutBtn}`}
              onClick={cikis}
              aria-label="Çıkış yap"
              title="Çıkış yap"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className={s.content}>
          <ScrollToTop />
          <Outlet context={{ yonetici }} />
        </main>
      </div>

      <nav className={s.bottomNav} aria-label="Hızlı gezinme">
        {altOgeler.map((o) => {
          const Ikon = ICONS[o.icon] || LayoutDashboard;
          if (!o.ready) {
            return (
              <span key={o.id} className={`${s.bnItem} ${s.bnOff}`} aria-disabled="true">
                <Ikon size={20} />
                <span className={s.bnLabel}>{o.short || o.label}</span>
              </span>
            );
          }
          return (
            <NavLink
              key={o.id}
              to={o.to}
              end={Boolean(o.end)}
              className={({ isActive }) => `${s.bnItem} ${isActive ? s.bnOn : ''}`}
            >
              <Ikon size={20} />
              <span className={s.bnLabel}>{o.short || o.label}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          className={`${s.bnItem} ${cekmece ? s.bnOn : ''}`}
          onClick={() => setCekmece(true)}
        >
          <Menu size={20} />
          <span className={s.bnLabel}>Menü</span>
        </button>
      </nav>
    </div>
  );
}
